import { useCallback, useEffect, useMemo } from "react";
import { Icon } from "semantic-ui-react";
import { Tooltip } from "components/common/tooltip";
import CheckboxField from "features/Library/Common/Fields/CheckboxField";
import { WithFormikPayload } from "features/Library/Common/Fields/WithFormikPayload";
import { type CommonComponentProps } from "features/Library/Common/props";
import { BRANCHING_QUESTION_ANSWERS_MAX_COUNT } from "features/Library/Surveys/types/constants";
import { useContentContext } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/state/ContentContext";
import {
  getAnswersCount,
  isAllowedBranchingQuestionType,
  nameofWithPrefix,
} from "features/Library/Surveys/SurveyDetails/SurveyContentForm/utils";
import { type SurveyQuestion } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/types";
import questionCheckboxStyles from "features/Library/Common/QuestionsForm/Questions/QuestionFields/CheckBox/Checkbox.module.scss";

export interface BranchingToggleProps extends CommonComponentProps {
  question: SurveyQuestion<any>;
  index: number;
}

const getBranchingQuestionTooltipMessage = (question: SurveyQuestion<any>) => {
  const count = getAnswersCount(question);
  if (count > BRANCHING_QUESTION_ANSWERS_MAX_COUNT) {
    return `Branching questions can have no more than ${BRANCHING_QUESTION_ANSWERS_MAX_COUNT} answers. Remove (${
      count - BRANCHING_QUESTION_ANSWERS_MAX_COUNT
    }) answer(s) to enable this toggle.`;
  }

  return "";
};

export const BranchingToggle = (props: BranchingToggleProps) => {
  const { validatedFieldProps, question, propertyNamePrefix, onChanged, disabled, index } = props;
  const { toggleBranchingQuestion, branchingQuestionId, turnOffBranchingQuestion } = useContentContext();
  const nameof = useMemo(() => {
    return nameofWithPrefix<SurveyQuestion<any>>(propertyNamePrefix);
  }, [propertyNamePrefix]);

  const isBranching = !!question.isBranching;
  const id = question.id!;

  useEffect(() => {
    return () => {
      turnOffBranchingQuestion(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isBranching || branchingQuestionId === id) {
      toggleBranchingQuestion(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBranching]);

  const { setFieldValue } = validatedFieldProps;
  const { isRequired } = question;

  const setValue = useCallback(
    async (field: string, value: boolean, shouldValidate?: boolean) => {
      await (setFieldValue(field, value, shouldValidate) as unknown as Promise<void>);
      if (value && !isRequired) {
        await (setFieldValue(nameof("isRequired"), true, shouldValidate) as unknown as Promise<void>);
      }
    },
    [isRequired, setFieldValue, nameof],
  );

  const shouldRender = branchingQuestionId === undefined || branchingQuestionId === id;

  const branchingDisabled =
    getAnswersCount(question) > BRANCHING_QUESTION_ANSWERS_MAX_COUNT ||
    !isAllowedBranchingQuestionType(question.questionType);

  const tooltipMessage = getBranchingQuestionTooltipMessage(question);

  return shouldRender ? (
    <div className={questionCheckboxStyles.container}>
      <WithFormikPayload
        propertyName={nameof("isBranching")}
        {...validatedFieldProps}
        render={(formikProps) => (
          <Tooltip
            target={
              <CheckboxField
                {...formikProps}
                toggle
                disableValidation
                index={index}
                value={isBranching}
                disabled={disabled || branchingDisabled}
                setFieldTouched={validatedFieldProps.setFieldTouched}
                setFieldValue={setValue}
                onChange={onChanged}
                label="Branching Question"
              />
            }
            content={tooltipMessage}
            position="top center"
            styles={{ width: "20rem" }}
            hideOnScroll
            showAlways={!!tooltipMessage}
          />
        )}
      />

      <Tooltip
        target={<Icon className="info circle" />}
        content={
          "Only one question per survey can be assigned as a branching question. A branching question cannot have more than 5 answers. "
        }
        position="top center"
        styles={{ width: "20rem" }}
        hideOnScroll
        showAlways
      />
    </div>
  ) : null;
};
